import { useState, useRef, useEffect } from "react";
import { FileText, Trash2 } from "lucide-react";
import { SB } from "@/config/sidebarSizing";
import { THEME } from "@/config/theme";
import { useViewportCtx } from "@/contexts/ViewportCtx";
import { useDocumentCtx } from "@/contexts/DocumentCtx";
import type { ApiForge, ApiProject } from "@/services/projectService";

interface Props {
  forge: ApiForge;
  project: ApiProject;
  siblingForges: ApiForge[];
  isActive: boolean;
  onRename: (forgeId: string, title: string) => void;
  onDelete: (forgeId: string) => void;
  onSelect?: () => void;
}

export default function LeafDocument({ forge, project, siblingForges, isActive, onRename, onDelete, onSelect }: Props) {
  const { setForgeId } = useViewportCtx();
  const { setDoc } = useDocumentCtx();
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(forge.title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    setEditTitle(forge.title);
  }, [forge.title]);

  const handleClick = () => {
    if (editing) return;
    setForgeId(forge.id);
    setDoc({
      forgeId: forge.id,
      title: forge.title,
      researchQuestion: forge.researchQuestion,
      projectId: project.id,
      projectName: project.name,
      siblingForges: siblingForges.map((s) => ({ id: s.id, title: s.title })),
    });
    onSelect?.();
  };

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditTitle(forge.title);
    setEditing(true);
  };

  const commitEdit = () => {
    setEditing(false);
    const title = editTitle.trim();
    if (title && title !== forge.title) {
      onRename(forge.id, title);
    } else {
      setEditTitle(forge.title);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`relative w-full flex items-center ${SB.row.leaf} rounded-lg text-left cursor-pointer group transition-colors duration-150 ease-out before:absolute before:left-0.5 before:top-1/2 before:-translate-y-1/2 before:h-4 before:w-[3px] before:rounded-full ${
        isActive
          ? "bg-indigo-500/15 text-slate-50 before:bg-indigo-400"
          : "text-slate-400 hover:bg-white/5 hover:text-slate-100 before:bg-transparent"
      }`}
    >
      <FileText size={SB.icon.file} className={`shrink-0 transition-colors duration-150 ${isActive ? "text-indigo-300" : "text-slate-500 group-hover:text-slate-300"}`} />
      {editing ? (
        <input
          ref={inputRef}
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitEdit();
            if (e.key === "Escape") { setEditing(false); setEditTitle(forge.title); }
          }}
          onClick={(e) => e.stopPropagation()}
          className={`flex-1 ${SB.text.forge} ${SB.font.sidebar} ${THEME.sidebar.input} rounded px-1 py-0 min-w-0`}
        />
      ) : (
        <span
          className={`${SB.text.forge} ${SB.font.sidebar} ${isActive ? "font-semibold" : "font-medium"} truncate flex-1 select-text`}
          onDoubleClick={startEdit}
          title="Double-click to rename"
        >
          {forge.title}
        </span>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(forge.id); }}
        title="Delete forge"
        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:text-red-400 transition-all shrink-0"
      >
        <Trash2 size={SB.icon.delete} />
      </button>
    </div>
  );
}
